import { verifyLogin } from "util/cache";
import { useDocTitle } from "util/hooks";
import { useGetClassesQuery } from "util/rtk-api";
import { shortGrade } from "util/grade-band";

import { ClassroomCard, CreateClassroomCard, ArchivedClassesCard } from "classes/classroom-card";
import { CreateClassroomModal } from "classes/create-class-overlay";
import { ArchivedClassesOverlay } from "classes/archived-classes-overlay";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Class } from "types";
import 'css/pages/classrooms.css'

type SortOption = 'name' | 'name-desc' | 'students'

export const Classrooms = () => {
    const [showCreateClassroomModal, setShowCreateClassroomModal] = useState(false)
    const [showArchivedOverlay, setShowArchivedOverlay] = useState(false)
    const { data, isLoading, error } = useGetClassesQuery(undefined)

    const [search, setSearch] = useState('')
    const [subjectFilter, setSubjectFilter] = useState('')
    const [gradeFilter, setGradeFilter] = useState('')
    const [periodFilter, setPeriodFilter] = useState('')
    const [sort, setSort] = useState<SortOption>('name')
    const firstUnnamedRef = useRef<HTMLDivElement>(null)

    const navigate = useNavigate()

    useEffect(() => {
        verifyLogin(navigate)
    }, [])

    useDocTitle('All for One - Classrooms')

    const classrooms: Class[] = data?.classes || []

    // Options for each filter are derived from the classrooms that actually
    // exist, not a hardcoded list -- a filter never offers a choice with zero
    // matching classrooms.
    const subjectOptions = useMemo(
        () => Array.from(new Set(classrooms.map(c => c.subject).filter(Boolean))).sort(),
        [classrooms]
    )
    const gradeOptions = useMemo(
        () => Array.from(new Set(classrooms.flatMap(c => c.grades || []))).sort(),
        [classrooms]
    )
    const periodOptions = useMemo(
        () => Array.from(new Set(classrooms.map(c => c.period).filter(Boolean))).sort((a, b) =>
            a === 'All Day' ? -1 : b === 'All Day' ? 1 : Number(a) - Number(b)
        ),
        [classrooms]
    )

    const nameCounts = useMemo(() => {
        const counts: Record<string, number> = {}
        classrooms.forEach(c => {
            const n = c.name?.trim().toLowerCase()
            if (n) counts[n] = (counts[n] || 0) + 1
        })
        return counts
    }, [classrooms])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        let list = classrooms.filter(c => {
            if (subjectFilter && c.subject !== subjectFilter) return false
            if (gradeFilter && !(c.grades || []).includes(gradeFilter)) return false
            if (periodFilter && c.period !== periodFilter) return false
            if (q) {
                const haystack = [c.name, c.subject, ...(c.grades || [])].join(' ').toLowerCase()
                if (!haystack.includes(q)) return false
            }
            return true
        })
        list = [...list].sort((a, b) => {
            if (sort === 'name') return (a.name || '').localeCompare(b.name || '')
            if (sort === 'name-desc') return (b.name || '').localeCompare(a.name || '')
            if (sort === 'students') return (b.students?.length || 0) - (a.students?.length || 0)
            return 0
        })
        return list
    }, [classrooms, search, subjectFilter, gradeFilter, periodFilter, sort])

    const anyFilter = !!(search || subjectFilter || gradeFilter || periodFilter)
    const unnamedCount = classrooms.filter(c => !c.name?.trim()).length

    const clearFilters = () => {
        setSearch('')
        setSubjectFilter('')
        setGradeFilter('')
        setPeriodFilter('')
    }

    const scrollToFirstUnnamed = () => {
        firstUnnamedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    // err-11: a failed fetch used to render as "you have no classes"
    if (error) {
        return (
            <div className="classrooms-page">
                <div className="classrooms">
                    <h1>My Classrooms</h1>
                    <p>We couldn't load your classes — they're safe. Check your connection and <a href="#retry" onClick={e => { e.preventDefault(); window.location.reload() }}>try again</a>.</p>
                </div>
            </div>
        )
    }

    let seenFirstUnnamed = false

    return (
        isLoading ?
            <div className="classrooms-page"><div>Loading...</div></div> :
        <div className="classrooms-page">
        <div className="classrooms">
            <div className="classrooms-page-head">
                <div className="classrooms-page-head-text">
                    <span className="classrooms-eyebrow">Your rooms</span>
                    <h1>My classrooms</h1>
                    <div className="classrooms-page-rule" />
                    <p>Every section you teach, in one place. Open a classroom to manage students, or start a new one below.</p>
                </div>
                <button className="classrooms-create-btn" onClick={() => setShowCreateClassroomModal(true)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Create classroom
                </button>
            </div>

            <div className="classrooms-toolbar">
                <div className="classrooms-search-wrap">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        className="classrooms-search-input"
                        placeholder="Search by name, subject or grade"
                        aria-label="Search classrooms"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <select
                    className={`classrooms-sel${subjectFilter ? ' is-set' : ''}`}
                    aria-label="Filter by subject"
                    value={subjectFilter}
                    onChange={(e) => setSubjectFilter(e.target.value)}
                >
                    <option value="">Subject</option>
                    {subjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    className={`classrooms-sel${gradeFilter ? ' is-set' : ''}`}
                    aria-label="Filter by grade"
                    value={gradeFilter}
                    onChange={(e) => setGradeFilter(e.target.value)}
                >
                    <option value="">Grade</option>
                    {gradeOptions.map(g => <option key={g} value={g}>{shortGrade(g)}</option>)}
                </select>
                <select
                    className={`classrooms-sel${periodFilter ? ' is-set' : ''}`}
                    aria-label="Filter by period"
                    value={periodFilter}
                    onChange={(e) => setPeriodFilter(e.target.value)}
                >
                    <option value="">Period</option>
                    {periodOptions.map(p => <option key={p} value={p}>{p === 'All Day' ? p : `Period ${p}`}</option>)}
                </select>
                <select
                    className="classrooms-sel"
                    aria-label="Sort classrooms"
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortOption)}
                >
                    <option value="name">Sort: A–Z</option>
                    <option value="name-desc">Sort: Z–A</option>
                    <option value="students">Sort: Most students</option>
                </select>
                {anyFilter && (
                    <button className="classrooms-clear-filters" onClick={clearFilters}>Clear filters</button>
                )}
            </div>

            <div className="classrooms-count-row" aria-live="polite">
                {anyFilter
                    ? `${filtered.length} of ${classrooms.length} classrooms match`
                    : `${classrooms.length} ${classrooms.length === 1 ? 'classroom' : 'classrooms'}`}
            </div>

            {unnamedCount > 0 && (
                <div className="classrooms-notice">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <p>
                        <strong>{unnamedCount} classroom{unnamedCount > 1 ? 's have' : ' has'} no name.</strong> Unnamed rooms are hard to pick out when you assign a lesson.
                    </p>
                    <button className="classrooms-notice-btn" onClick={scrollToFirstUnnamed}>Name them</button>
                </div>
            )}

            <div className="classrooms-grid">
                {filtered.length === 0 && classrooms.length > 0 && (
                    <div className="classrooms-empty">
                        <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <h3>No classrooms match those filters</h3>
                        <p>Try a broader search, or clear the filters to see everything.</p>
                        <button className="classrooms-create-btn" onClick={clearFilters}>Clear filters</button>
                    </div>
                )}
                {filtered.map((classroom) => {
                    const isUnnamed = !classroom.name?.trim()
                    const isFirstUnnamed = isUnnamed && !seenFirstUnnamed
                    if (isFirstUnnamed) seenFirstUnnamed = true
                    return (
                        <div key={classroom.classId} ref={isFirstUnnamed ? firstUnnamedRef : undefined}>
                            <ClassroomCard
                                classroom={classroom}
                                hasDuplicateName={!!classroom.name?.trim() && nameCounts[classroom.name.trim().toLowerCase()] > 1}
                            />
                        </div>
                    )
                })}
                <ArchivedClassesCard onClick={() => setShowArchivedOverlay(true)} />
                <CreateClassroomCard handleCreateClassroom={() => { setShowCreateClassroomModal(true) }} />
            </div>

            {showCreateClassroomModal && <CreateClassroomModal setShowCreateClassroomModal={setShowCreateClassroomModal} />}
            {showArchivedOverlay && <ArchivedClassesOverlay onDismiss={() => setShowArchivedOverlay(false)} />}
        </div>
        </div>
    )
}
